import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import Skeleton from '@material-ui/lab/Skeleton';

import JobTable from '../JobTable';
import ReopenButton from './ReopenButton';
import DefaultColumnFilter from '../../react-table/DefaultColumnFilter';
import TableToolbar from '../../react-table/TableToolbar';
import model from '../assets/models/model';
import formatClosedJobs from '../functions/formatClosedJobs';
import Metrics from '../metrics/Metrics';
import metricModel, { toDays } from '../metrics/metricModel';
import useMetrics from '../hooks/useMetrics';
import Charts from '../charts/Charts';
import {
  FILLED_CLOSED_OPTION_CLOSED,
  IS_OPEN_TRUE,
} from '../assets/constants/jobConstants';

const SKELETON_ROWS = 5;

const AdminClosedJob = ({
  jobs,
  isLoading,
  onOpen,
  selectedJobToReopen,
  refs,
}) => {
  const [closedJobs, setClosedJobs] = useState([]);
  const closedFilledMetricsRef = React.useRef();

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter, // sets up default column filter input type
    }),
    []
  );

  const defaultHiddenColumns = React.useMemo(() => {
    return [
      model.hourlyRate.id,
      model.shift.id,
      model.schedule.id,
      model.teleworking.id,
      model.usCitizenshipRequired.id,
      model.travel.id,
      model.travelPercentage.id,
      model.educationRequired.id,
      model.majorSpecialization.id,
      model.jobDescription.id,
    ];
  }, []);

  // Initial state of table parameters
  const initialState = React.useMemo(
    () => ({
      pageSize: 25,
      sortBy: [
        {
          id: model?.jobId?.id,
          desc: true,
        },
      ],
      hiddenColumns: defaultHiddenColumns,
    }),
    [defaultHiddenColumns]
  );

  const columns = React.useMemo(() => {
    return [
      {
        Header: model.jobId.name,
        accessor: model.jobId.id,
      },
      {
        Header: model.jobTitle.name,
        accessor: model.jobTitle.id,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          // eslint-disable-next-line react/prop-types
          <div style={{ maxWidth: 200 }}>{row.original.jobTitle}</div>
        ),
        reportColumnWidth: '20%',
      },
      {
        Header: model.jobLocation.name,
        accessor: model.jobLocation.id,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          // eslint-disable-next-line react/prop-types
          <div style={{ maxWidth: 200 }}>{row.original.jobLocation}</div>
        ),
        reportColumnWidth: '20%',
      },
      {
        Header: model.minimumClearance.name,
        accessor: model.minimumClearance.id,
      },
      {
        Header: model.polygraphType.name,
        accessor: model.polygraphType.id,
      },
      {
        Header: model.postingDate.name,
        accessor: 'formattedPostingDate',
      },
      {
        Header: model.closedFilledDate.name,
        accessor: 'formattedClosedFilledDate',
      },
      {
        Header: model.closedFilled.name,
        accessor: model.closedFilled.id,
      },
      {
        id: 'Reopen',
        disableFilters: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row: { original } }) => {
          // eslint-disable-next-line react/prop-types
          if (original.closedFilled === FILLED_CLOSED_OPTION_CLOSED) {
            return (
              <ReopenButton
                row={original}
                selectedJobToReopen={selectedJobToReopen}
              />
            );
          }
          return null;
        },
      },
      {
        id: model.hourlyRate.id,
        accessor: model.hourlyRate.id,
      },
      {
        id: model.shift.id,
        accessor: model.shift.id,
      },
      {
        id: model.schedule.id,
        accessor: model.schedule.id,
      },
      {
        id: model.teleworking.id,
        accessor: model.teleworking.id,
      },
      {
        id: model.usCitizenshipRequired.id,
        accessor: model.usCitizenshipRequired.id,
      },
      {
        id: model.travel.id,
        accessor: model.travel.id,
      },
      {
        id: model.travelPercentage.id,
        accessor: model.travelPercentage.id,
      },
      {
        id: model.educationRequired.id,
        accessor: model.educationRequired.id,
      },
      {
        id: model.majorSpecialization.id,
        accessor: model.majorSpecialization.id,
      },
      {
        id: model.jobDescription.id,
        accessor: model.jobDescription.id,
      },
    ];
  }, [selectedJobToReopen]);

  const tableData = React.useMemo(
    () =>
      isLoading ? Array(SKELETON_ROWS).fill({}) : formatClosedJobs(closedJobs),
    [isLoading, closedJobs]
  );

  const tableColumns = React.useMemo(
    () =>
      isLoading
        ? columns.map((column) => ({
            ...column,
            Cell: <Skeleton />,
          }))
        : columns,
    [isLoading, columns]
  );

  const tableHookProps = useTable(
    {
      columns: tableColumns,
      data: tableData,
      defaultColumn,
      initialState,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const {
    rows,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { selectedRowIds, globalFilter },
  } = tableHookProps;

  const filteredJobs = React.useMemo(
    () => rows.map((item) => item.original),
    [rows]
  );

  const {
    totalClosed,
    totalFilled,
    totalClosedFilled,
    qualifiedApplicants,
    averageNumberApplicants,
    averageTimeToClose,
    averageTimeToFill,
    averageNumberInterviews,
    offerAcceptance,
  } = useMetrics({ closedJobs: filteredJobs });

  const gadgets = [
    {
      id: 'ClosedFilledTotal',
      metric: [totalClosed, totalFilled, totalClosedFilled],
      attributes: {
        label: 'Closed / Filled / Total',
        formatter: (array) => `${array.join(' / ')} jobs`,
      },
    },
    {
      id: 'qualifiedApplicants',
      metric: qualifiedApplicants,
      attributes: metricModel.qualifiedApplicants,
    },
    {
      id: 'averageNumberApplicants',
      metric: averageNumberApplicants,
      attributes: metricModel.averageNumberApplicants,
    },
    {
      id: 'averageTime',
      metric: [
        toDays(averageTimeToClose).toFixed(0),
        toDays(averageTimeToFill).toFixed(0),
      ],
      attributes: {
        label: 'Avg. Time to Close / Fill',
        formatter: (array) => `${array.join(' / ')} days`,
      },
    },
    {
      id: 'averageNumberInterviews',
      metric: averageNumberInterviews,
      attributes: metricModel.averageNumberInterviews,
    },
    {
      id: 'offerAcceptance',
      metric: offerAcceptance,
      attributes: metricModel.offerAcceptance,
    },
  ];

  const reportColumns = columns.filter(
    (column) =>
      !defaultHiddenColumns.includes(column.accessor) &&
      typeof column.id === 'undefined'
  );

  useEffect(() => {
    if (jobs) {
      setClosedJobs(
        jobs.filter((job) => job[model.isOpen.id] !== IS_OPEN_TRUE)
      );
    }
  }, [jobs]);

  const { metricsRef, tableDataRef, allDataRef } = refs;

  useEffect(() => {
    tableDataRef.current = {
      type: 'closed-jobs',
      columns: reportColumns,
      // eslint-disable-next-line react/prop-types
      data: rows.map((row) => row.values),
    };
    allDataRef.current = {
      type: 'closed-jobs',
      data: filteredJobs,
    };
    metricsRef.current = closedFilledMetricsRef.current;
  }, [rows, reportColumns, filteredJobs, tableDataRef, metricsRef, allDataRef]);

  return (
    <>
      <div>
        <div id="closed-filled-metrics" ref={closedFilledMetricsRef}>
          <Metrics gadgets={gadgets} />
          <Charts closedJobs={filteredJobs} />
        </div>
        <TableToolbar
          numSelected={Object.keys(selectedRowIds).length}
          preGlobalFilteredRows={preGlobalFilteredRows}
          setGlobalFilter={setGlobalFilter}
          globalFilter={globalFilter}
        />
        <JobTable
          tableHookProps={tableHookProps}
          tableColumns={tableColumns}
          onOpen={onOpen}
          columnClicksToIgnore={['Reopen']}
        />
      </div>
    </>
  );
};

AdminClosedJob.defaultProps = {
  jobs: [],
  isLoading: true,
};

AdminClosedJob.propTypes = {
  jobs: PropTypes.any,
  isLoading: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  selectedJobToReopen: PropTypes.func.isRequired,
  refs: PropTypes.object.isRequired,
};

export default AdminClosedJob;
